<template>
	<section class="devices-container">
		<MaintenanceModal
			v-if="isMaintenanceModalOpen"
			:key="maintenanceModalKey"
			:initialType="maintenanceInitialType"
			@saveDevice="saveDevice"
			@saveMaintenanceRecord="handleSaveMaintenanceRecord"
			@validationError="handleValidationError"
			@loadMaintenanceHistory="handleLoadMaintenanceHistory"
			@closeModal="closeModal"
			:modalTexts="modalTexts"
			:currDevice="currDevice"
		/>

		<!-- Errors Modal -->
		<ErrorsModal
			v-if="isErrorsModalOpen"
			@saveDevice="saveDevice"
			@closeModal="closeModal"
			:modalTexts="modalTexts"
			:currDevice="currDevice"
		/>

		<DeviceQrScannerModal
			v-if="scanSensor"
			:currentDeviceCode="scanSensor.device_code"
			:currentSensorId="scanSensor.id"
			:isAdmin="loggedUser && loggedUser.type === 'admin'"
			@close="closeScanModal"
			@submit="onScanDeviceSubmit"
		/>

		<Tabs :isRTL="isRTL">
			<Tab :label="$t('status')" :to="'/admin/devices'" exact>
				<DeviceStatusList
					:devicesStatus="devicesStatus"
					v-if="devicesStatus && devicesStatus.length > 0 && !isLoading"
					@cellClick="cellClick"
					@scanDevice="openScanModal"
				/>
				<Loading v-if="isLoading || devicesStatus === null || devicesStatus === undefined" class="loader" />
				<div v-else-if="devicesStatus && devicesStatus.length === 0" class="no-data-message">
					{{ $t('no_data_available') }}
				</div>
			</Tab>
			<Tab :label="$t('network')" :to="'/admin/devices/network'">
				<DeviceNetworkInfo />
			</Tab>
			<Tab :label="$t('connectivity')" :to="'/admin/devices/connectivity'">
				<DeviceConnectivity
					:devicesConnectivity="devicesConnectivity"
					v-if="!isDevicesConnectivityEmpty && !isConnectivityLoading"
					@updateFilterTime="updateFilterTime"
				/>
				<Loading v-if="isConnectivityLoading" class="loader" />
				<div v-else-if="isDevicesConnectivityEmpty">
					{{ $t('no_data_available') }}
				</div>
			</Tab>
			<Tab :label="$t('power')" :to="'/admin/devices/power'">
				<DeviceBatteryPower
					:devicesBatteryPower="devicesBatteryPower"
					v-if="!isDevicesBatteryPowerEmpty && !isPowerLoading"
				/>
				<Loading v-if="isPowerLoading" class="loader" />
				<div v-else-if="isDevicesBatteryPowerEmpty">
					{{ $t('no_data_available') }}
				</div>
			</Tab>
		</Tabs>
	</section>
</template>

<script>
import DeviceStatusList from '../components/DeviceStatusList'
import DeviceQrScannerModal from '../components/DeviceQrScannerModal'
import DeviceConnectivity from '../components/DeviceConnectivity'
import DeviceNetworkInfo from '../components/DeviceNetworkInfo'
import MaintenanceModal from '../components/MaintenanceModal'
import ErrorsModal from '../components/ErrorsModal'
import DeviceBatteryPower from '../components/DeviceBatteryPower'
import { mapGetters } from 'vuex'
import Loading from '@/modules/common/components/Loading'
import Tabs from '@/modules/common/components/Tabs/Tabs'
import Tab from '@/modules/common/components/Tabs/Tab'
import sensorService from '../../control-panel/services/sensorService'
import swalService from '@/modules/common/services/swalService'
import { MAINTENANCE_SAVE_MESSAGE_KEYS } from '../utils/maintenanceUtils'
import { getDeviceCodeConflictErrorMessage, getDeviceCodeLookupErrorMessage, replaceSensorDeviceCode } from '../utils/deviceCodeUtils'
import io from 'socket.io-client/dist/socket.io'
const SOCKET_URL = process.env.NODE_ENV === 'production' ? '/' : 'http://localhost:3000'

const MAINTENANCE_MODAL_TEXTS = {
	note: {
		headerPrimary: 'Note',
		headerSecondary: true,
		footerConfirm: 'Add',
		titleKey: 'general_note',
		confirmTextKey: 'save',
	},
	battery: {
		titleKey: 'battery_replacement',
		confirmTextKey: 'save_replacement',
	},
}

export default {
	data() {
		return {
			isLoading: false,
			isConnectivityLoading: false,
			modalTexts: null,
			currDevice: null,
			isPowerLoading: false,
			maintenanceInitialType: 'note',
			scanSensor: null,
		}
	},
	methods: {
		async updateSiteInfo() {
			this.isLoading = true
			const { site: siteId } = this.filterSelected
			this.$store.commit({ type: 'resetDevicesConnectivity' })
			this.$store.commit({ type: 'resetDevicesBatteryPower' })
			await this.$store.dispatch({ type: 'getDevices', siteId })
			this.connectSocket()
			this.isLoading = false
		},
		connectSocket() {
			if (this.socket) this.socket.disconnect()
			const { site: siteId } = this.filterSelected
			this.socket = io.connect(SOCKET_URL, {
				transports: ['websocket'],
				pollingInterval: 10000
			})
			this.socket.emit('device-list', { siteId: siteId })
			this.socket.on('updateDeviceList', () => this.$store.dispatch({ type: 'getDevices', siteId }))
		},
		async updateConnectivity() {
			this.isConnectivityLoading = true
			const { site: siteId, time: dates } = this.filterSelected
			await this.$store.dispatch({ type: 'getDevicesConnectivity', siteId, dates })
			this.isConnectivityLoading = false
		},
		async updateBatteryPower() {
			this.isPowerLoading = true
			await this.$store.dispatch({ type: 'getDevicesBatteryPower', filter: this.filterSelected })
			this.isPowerLoading = false
		},
		updateFilterTime(filterTime) {
			this.$store.commit({
				type: 'updateFilter',
				field: 'timeType',
				value: 'custom_time'
			})
			this.$store.commit({
				type: 'updateFilter',
				field: 'time',
				value: filterTime
			})
		},
		async cellClick(device, type) {
			this.currDevice = device
			if (type === 'errors') {
				this.addErrorsModalInfo()
				return
			}

			this.openMaintenanceModal(type, device)
		},
		openMaintenanceModal(type, device) {
			const roomName = device.room_name
			const config = MAINTENANCE_MODAL_TEXTS[type] || MAINTENANCE_MODAL_TEXTS.note

			this.maintenanceInitialType = type
			this.modalTexts = {
				title: `${this.$t(config.titleKey)} - ${roomName}`,
				confirmText: this.$t(config.confirmTextKey),
				cancelText: this.$t('cancel'),
				...(config.headerPrimary ? { headerPrimary: config.headerPrimary } : {}),
				...(config.headerSecondary ? { headerSecondary: roomName } : {}),
				...(config.footerConfirm ? { footerConfirm: config.footerConfirm } : {}),
			}
		},
		addErrorsModalInfo() {
			const { room_name } = this.currDevice
			this.modalTexts = {
				headerPrimary: `${this.$t('latest_errors')}`,
				headerSecondary: room_name
			}
		},
		async saveDevice() {
			delete this.currDevice.room_name
			try {
				await sensorService.updateSensor(this.currDevice)
				const { site: siteId } = this.filterSelected
				await this.$store.dispatch({ type: 'getDevices', siteId })
				swalService.savedMsg(this.$t('Your work has been saved'))
			} catch (err) {
				swalService.errorMsg()
			}
			this.closeModal()
		},
		async handleSaveMaintenanceRecord({ maintenanceType, data }) {
			try {
				await sensorService.saveSensorNote({
					...data,
					site_id: data.site_id || this.filterSelected.site,
				})
				swalService.savedMsg(this.$t(MAINTENANCE_SAVE_MESSAGE_KEYS[maintenanceType]))
				const { site: siteId } = this.filterSelected
				await this.$store.dispatch({ type: 'getDevices', siteId })
			} catch (error) {
				console.error(`Error saving ${maintenanceType} maintenance record:`, error)
				swalService.errorMsg()
			}
			this.closeModal()
		},
		handleValidationError(message) {
			swalService.errorMsg(message)
		},
		async handleLoadMaintenanceHistory({ sensorCode, maintenanceType, callback }) {
			try {
				const history = await sensorService.getMaintenanceHistory(sensorCode, maintenanceType)
				callback(history, null)
			} catch (error) {
				callback([], error)
			}
		},
		closeModal() {
			this.currDevice = null
			this.modalTexts = null
			this.maintenanceInitialType = 'note'
		},
		async openScanModal(deviceId) {
			try {
				const sensor = await sensorService.getByDeviceCode(deviceId, { silent: true })
				if (!sensor?.device_code) {
					swalService.errorMsg(this.$t('device_code_open_failed'))
					return
				}
				this.scanSensor = sensor
			} catch (err) {
				const status = err?.status
				const explained = status === 403 || status == null || status >= 500
				swalService.errorMsg(explained
					? getDeviceCodeLookupErrorMessage({ error: err }, (key) => this.$t(key))
					: this.$t('device_code_open_failed'))
			}
		},
		closeScanModal() {
			this.scanSensor = null
		},
		async onScanDeviceSubmit({ newDeviceCode }) {
			const oldCode = this.scanSensor.device_code
			const sensor = this.scanSensor
			this.closeScanModal()

			const confirmResult = await swalService.confirmReplaceDeviceCode({
				title: this.$t('confirm_replace_device_code'),
				text: this.$t('confirm_replace_device_code_body')
					.replace('{oldCode}', oldCode)
					.replace('{newCode}', newDeviceCode),
				confirmButtonText: this.$t('OK'),
				cancelButtonText: this.$t('cancel'),
			})
			if (!confirmResult.value) {
				this.scanSensor = sensor
				return
			}

			try {
				await replaceSensorDeviceCode(oldCode, newDeviceCode, sensorService, sensor)
				swalService.savedMsg(this.$t('Your work has been saved'))
				const { site: siteId } = this.filterSelected
				await this.$store.dispatch({ type: 'getDevices', siteId })
			} catch (err) {
				const conflictMessage = getDeviceCodeConflictErrorMessage(
					err,
					this.loggedUser?.type === 'admin',
					(key) => this.$t(key),
				)
				if (conflictMessage) {
					swalService.errorMsg(conflictMessage)
					this.scanSensor = sensor
					return
				}
				swalService.errorMsg(this.$t(err?.status === 403 ? 'device_code_save_forbidden' : 'device_code_save_failed'))
				this.scanSensor = sensor
			}
		},
	},
	computed: {
		...mapGetters([
			'isRTL',
			'devicesStatus',
			'devicesConnectivity',
			'devicesBatteryPower',
			'filterSelected',
			'loggedUser',
		]),
		isDevicesConnectivityEmpty() {
			return Object.keys(this.devicesConnectivity).length === 0
		},
		maintenanceModalKey() {
			if (!this.currDevice) return 'maintenance-closed'
			const deviceId = this.currDevice.device_id || this.currDevice.sensor_code || 'device'
			return `${deviceId}-${this.maintenanceInitialType}`
		},
		isMaintenanceModalOpen() {
			if (!this.currDevice || !this.modalTexts?.title) return false
			if (Array.isArray(this.currDevice.latest_errors)) return false
			return true
		},
		isErrorsModalOpen() {
			if (!this.currDevice) return false
			const { latest_errors } = this.currDevice
			return latest_errors && this.modalTexts
		},
		isDevicesBatteryPowerEmpty() {
			return Object.keys(this.devicesBatteryPower).length === 0
		}
	},
	watch: {
		'filterSelected.site': {
			async handler() {
				const prms = []
				prms.push(this.updateSiteInfo())
				if (this.$route.path.includes('connectivity')) {
					prms.push(this.updateConnectivity())
				}
				if (this.$route.path.includes('power')) {
					prms.push(this.updateBatteryPower())
				}
				await Promise.all(prms)
			},
			immediate: true
		},
		'filterSelected.time': {
			async handler() {
				if (!this.isDevicesConnectivityEmpty && !this.isDevicesBatteryPowerEmpty) {
					await Promise.all([this.updateConnectivity(), this.updateBatteryPower()])
				} else if (!this.isDevicesConnectivityEmpty) {
					await this.updateConnectivity()
				} else if (!this.isDevicesBatteryPowerEmpty) {
					await this.updateBatteryPower()
				}
			}
		},
		'$route.path': {
			async handler() {
				if (this.$route.path.includes('connectivity') && this.isDevicesConnectivityEmpty) {
					await this.updateConnectivity()
				}
				if (this.$route.path.includes('power') && this.isDevicesBatteryPowerEmpty) {
					await this.updateBatteryPower()
				}
			},
			immediate: true
		}
	},
	destroyed() {
		if (this.socket) this.socket.disconnect()
	},
	components: {
		DeviceStatusList,
		DeviceConnectivity,
		DeviceBatteryPower,
		DeviceNetworkInfo,
		Loading,
		Tabs,
		Tab,
		MaintenanceModal,
		ErrorsModal,
		DeviceQrScannerModal
	}
}
</script>
<style lang="scss" scoped>
@use '@/styles/mixins' as *;

.devices-container {
	width: 100%;
	max-width: 100%;
	overflow-x: hidden;
	box-sizing: border-box;

	::v-deep .md-tabs {
		width: 100%;
		max-width: 100%;
	}

	::v-deep .md-tabs-content {
		padding: 0;
		overflow-x: hidden;
		max-width: 100%;
	}
}

.top-row {
	display: flex;
	width: 100%;
	justify-content: space-between;
}

.loader {
	position: relative;
	z-index: 1;
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: 200px;
	width: 100%;
}

.no-data-message {
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: 200px;
	width: 100%;
	max-width: 100%;
	padding: 40px 16px;
	font-size: 16px;
	color: #666;
	text-align: center;
	box-sizing: border-box;

	@media (max-width: 480px) {
		padding: 24px 12px;
		font-size: 14px;
	}
}
</style>
